/**
 * Recurring deductions — two-step model.
 *   1. Define a deduction TYPE (deduction_master).
 *   2. Assign it to employees (multi-select) with an optional per-person
 *      amount override.
 * Statutory types (tax / SSO / PF) are computed by the payroll engine and
 * shown read-only for visibility.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useDeductionMaster,
  useUpsertDeductionMaster,
  useDeleteDeductionMaster,
  useDeductionEligibility,
  useUpsertDeductionEligibility,
  useDeleteDeductionEligibility,
  useEmployees,
  type DeductionMasterRow,
  type DeductionEligibilityRow,
} from '../../hooks';
import { Plus, X, Sparkles, Users, Trash2, Pencil } from 'lucide-react';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

type EmpRow = { emp_code: string; first_name: string; last_name: string; nickname?: string | null };

const EMPTY: Partial<DeductionMasterRow> = { kind: 'fixed', name_th: '', code: '', default_amount: null, active: true };

// Suggested "other" recurring deductions (all kind=fixed). code must be
// unique per tenant; already-added ones are filtered out.
const SUGGESTIONS: Array<{ code: string; name_th: string; default_amount: number | null; note: string }> = [
  { code: 'company_loan', name_th: 'เงินกู้ / เงินยืมบริษัท', default_amount: null, note: 'หักคืนเงินที่บริษัทให้ยืมหรือเบิกล่วงหน้า' },
  { code: 'housing',      name_th: 'ค่าหอพัก / ที่พัก',        default_amount: null, note: 'ค่าที่พักที่บริษัทจัดให้พนักงาน' },
  { code: 'meal_canteen', name_th: 'ค่าอาหาร (โรงอาหาร)',     default_amount: null, note: 'หักค่าอาหารที่ทานในโรงอาหารบริษัท' },
  { code: 'uniform',      name_th: 'ค่าชุดยูนิฟอร์ม',          default_amount: null, note: 'ค่าชุดพนักงาน / อุปกรณ์การทำงาน' },
  { code: 'late_fine',    name_th: 'ค่าปรับมาสาย / ขาดงาน',    default_amount: null, note: 'ค่าปรับตามระเบียบบริษัท' },
  { code: 'damage',       name_th: 'ค่าเสียหาย / ของเสีย',      default_amount: null, note: 'ชดใช้ทรัพย์สินหรือสินค้าที่เสียหาย' },
  { code: 'deposit',      name_th: 'เงินค้ำประกันการทำงาน',     default_amount: null, note: 'หักสะสมเป็นเงินประกัน คืนเมื่อลาออก' },
  { code: 'coop',         name_th: 'เงินสะสมสหกรณ์',            default_amount: null, note: 'เงินสมทบสหกรณ์ออมทรัพย์' },
  { code: 'welfare_fund', name_th: 'เงินฌาปนกิจ / กองทุนสวัสดิการ', default_amount: null, note: 'เงินสมทบกองทุนช่วยเหลือพนักงาน' },
  { code: 'phone_excess', name_th: 'ค่าโทรศัพท์เกินสิทธิ์',      default_amount: null, note: 'ส่วนที่เกินวงเงินที่บริษัทออกให้' },
  { code: 'extra_tax',    name_th: 'ภาษีหักเพิ่มเติม',          default_amount: null, note: 'หักภาษีเพิ่มตามที่พนักงานร้องขอ' },
];

function firstOfThisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// === Assign-employees panel for one fixed deduction type ===
function AssignModal({ master, onClose }: { master: DeductionMasterRow; onClose: () => void }) {
  const { data: employees } = useEmployees() as { data: EmpRow[] | undefined };
  const { data: elig } = useDeductionEligibility({ deduction: master.id });
  const upsert = useUpsertDeductionEligibility();
  const remove = useDeleteDeductionEligibility();

  const byEmp = new Map<string, DeductionEligibilityRow>();
  for (const e of elig ?? []) if (e.active) byEmp.set(e.emp_code, e);

  async function toggle(emp_code: string, on: boolean) {
    const existing = byEmp.get(emp_code);
    if (on && !existing) {
      await upsert.mutateAsync({ emp_code, deduction_id: master.id, start_date: firstOfThisMonth(), amount: null, active: true });
    } else if (!on && existing) {
      await remove.mutateAsync(existing.id);
    }
  }
  async function setAmount(emp_code: string, value: string) {
    const existing = byEmp.get(emp_code);
    if (!existing) return;
    const amount = value.trim() === '' ? null : Number(value);
    await upsert.mutateAsync({ id: existing.id, emp_code, deduction_id: master.id, start_date: existing.start_date, amount, active: true });
  }

  const list = (employees ?? []).filter((e) => e.emp_code);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h2 className="font-semibold">เลือกพนักงาน</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          {master.name_th}
          {master.default_amount != null && <> · ค่าเริ่มต้น {fmt(master.default_amount)} บาท/เดือน</>}
        </p>
        <div className="space-y-1">
          {list.map((e) => {
            const row = byEmp.get(e.emp_code);
            const checked = !!row;
            return (
              <div key={e.emp_code} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${checked ? 'bg-brand-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(ev) => toggle(e.emp_code, ev.target.checked)}
                  className="w-4 h-4 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{e.first_name} {e.last_name}{e.nickname ? ` (${e.nickname})` : ''}</div>
                  <div className="text-[11px] text-gray-400 font-mono">{e.emp_code}</div>
                </div>
                {checked && (
                  <input
                    type="number"
                    step="0.01"
                    defaultValue={row?.amount ?? ''}
                    placeholder={master.default_amount != null ? fmt(master.default_amount) : 'จำนวน'}
                    onBlur={(ev) => setAmount(e.emp_code, ev.target.value)}
                    className="w-24 rounded-lg border border-gray-300 px-2 py-1 text-xs text-right"
                  />
                )}
              </div>
            );
          })}
          {list.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">ยังไม่มีพนักงาน</p>}
        </div>
        <p className="text-[11px] text-gray-400 mt-3">เว้นช่องจำนวนว่างไว้ = ใช้ค่าเริ่มต้นของประเภทนี้ · ติ๊กออก = หยุดหัก</p>
      </div>
    </div>
  );
}

// === Add / edit a deduction type ===
function TypeModal({ initial, onClose }: { initial: Partial<DeductionMasterRow>; onClose: () => void }) {
  const [form, setForm] = useState<Partial<DeductionMasterRow>>(initial);
  const upsert = useUpsertDeductionMaster();
  const toast = useToast();

  async function save() {
    if (!form.code || !form.name_th) { toast.error('กรอกชื่อรายการ'); return; }
    try {
      await upsert.mutateAsync(form);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">{form.id ? 'แก้ไข' : 'เพิ่ม'}ประเภทเงินหัก</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">ชื่อรายการ *</label>
            <input className="input" value={form.name_th ?? ''} onChange={(e) => setForm({ ...form, name_th: e.target.value })} placeholder="เช่น ค่าหอพัก" />
          </div>
          <div>
            <label className="label">รหัส (code) *</label>
            <input className="input font-mono" value={form.code ?? ''} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!form.id} placeholder="housing" />
          </div>
          <div>
            <label className="label">จำนวนเริ่มต้น (บาท/เดือน)</label>
            <input type="number" step="0.01" className="input" value={form.default_amount ?? ''} onChange={(e) => setForm({ ...form, default_amount: e.target.value ? Number(e.target.value) : null })} placeholder="เว้นว่างได้ — กำหนดรายคนทีหลัง" />
          </div>
          <div>
            <label className="label">หมายเหตุ</label>
            <input className="input" value={form.note ?? ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <button onClick={save} disabled={upsert.isPending} className="btn-primary w-full">
            {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Content-only version used by the Wage hub. */
export function OwnerDeductionsContent() {
  const { data, isLoading } = useDeductionMaster();
  const upsert = useUpsertDeductionMaster();
  const remove = useDeleteDeductionMaster();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Partial<DeductionMasterRow> | null>(null);
  const [assigning, setAssigning] = useState<DeductionMasterRow | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  if (isLoading) return <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>;

  const rows = data ?? [];
  const existingCodes = new Set(rows.map((r) => r.code));
  const available = SUGGESTIONS.filter((s) => !existingCodes.has(s.code));

  async function addSuggestion(s: typeof SUGGESTIONS[number]) {
    await upsert.mutateAsync({ code: s.code, name_th: s.name_th, kind: 'fixed', default_amount: s.default_amount, note: s.note, active: true });
  }
  async function toggleActive(r: DeductionMasterRow) {
    await upsert.mutateAsync({ id: r.id, code: r.code, name_th: r.name_th, kind: r.kind, default_amount: r.default_amount, note: r.note, active: !r.active });
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-sm text-gray-600">รายการหักประจำของพนักงาน — เปิด/ปิดได้เฉพาะกลุ่ม</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => setShowSuggestions((v) => !v)} className="btn-secondary btn-slim"><Sparkles className="w-3.5 h-3.5" /> รายการแนะนำ</button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary btn-slim"><Plus className="w-3.5 h-3.5" /> เพิ่มเอง</button>
        </div>
      </div>
      {showSuggestions && (
        <div className="card p-3 mb-3">
          <p className="text-xs text-gray-500 mb-2">แตะเพื่อเพิ่มเป็นประเภทเงินหัก แล้วค่อยเลือกพนักงาน</p>
          <div className="flex flex-wrap gap-1.5">
            {available.map((s) => (
              <button key={s.code} title={s.note} onClick={() => addSuggestion(s)}
                className="text-xs border border-brand-300 bg-white rounded-full px-3 py-1.5 hover:bg-brand-50 transition flex items-center gap-1">
                <Plus className="w-3 h-3" /> {s.name_th}
              </button>
            ))}
            {available.length === 0 && <p className="text-xs text-gray-400">เพิ่มครบทุกรายการแนะนำแล้ว</p>}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mb-2">ทุกรายการเป็นตัวเลือก — เปิด/ปิดด้วยสวิตช์ แก้ไขค่า แล้วเลือกพนักงานที่ใช้รายการนั้น</p>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.id} className={`card p-3 flex items-center gap-2 ${r.active ? '' : 'opacity-55'}`}>
            {/* on/off toggle */}
            <button
              onClick={() => toggleActive(r)}
              className={`relative w-10 h-6 rounded-full transition shrink-0 ${r.active ? 'bg-brand-600' : 'bg-gray-300'}`}
              title={r.active ? 'เปิดใช้อยู่ — แตะเพื่อปิด' : 'ปิดอยู่ — แตะเพื่อเปิด'}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${r.active ? 'left-[18px]' : 'left-0.5'}`} />
            </button>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm flex items-center gap-1.5">
                {r.name_th}
                {r.kind === 'statutory' && <span className="badge bg-gray-100 text-gray-500 text-[10px]">คำนวณอัตโนมัติ</span>}
              </p>
              <p className="text-xs text-gray-400">
                {r.default_amount != null ? `${fmt(r.default_amount)} บาท/เดือน` : (r.kind === 'statutory' ? 'ระบบคำนวณให้' : 'กำหนดจำนวนรายคน')}
                {r.note ? ` · ${r.note}` : ''}
              </p>
            </div>
            {r.kind !== 'statutory' && (
              <button onClick={() => setAssigning(r)} className="btn-secondary btn-slim shrink-0"><Users className="w-3.5 h-3.5" /> พนักงาน</button>
            )}
            <button onClick={() => setEditing(r)} className="p-1 text-gray-400 hover:text-brand-600 shrink-0"><Pencil className="w-4 h-4" /></button>
            <button onClick={async () => { if (await confirm({ title: `ลบ "${r.name_th}"?`, danger: true, confirmText: 'ลบ' })) await remove.mutateAsync(r.id); }} className="p-1 text-gray-400 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">ยังไม่มีรายการหัก — กด "รายการแนะนำ" หรือ "เพิ่มเอง"</p>}
      </div>

      {editing && <TypeModal initial={editing} onClose={() => setEditing(null)} />}
      {assigning && <AssignModal master={assigning} onClose={() => setAssigning(null)} />}
    </>
  );
}

/** Standalone page kept for legacy /owner/deductions (now redirects via Wage hub). */
export function OwnerDeductions() {
  return (
    <AdminLayout title="รายการหักประจำ">
      <OwnerDeductionsContent />
    </AdminLayout>
  );
}
